import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Button, Stack, Typography, useTheme, alpha } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CheckboxGroup from '../../checkBoxGroup';
import RadioGroup from '../../raidioGroup';
import TextField from '../../textField';

type Props = {
  questions: Record<string, any>;
  userResponses: Record<string, any>;
  maxHeight: number;
  onUserResponseChange: (questionId: string, response: any) => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
};

const StepMainReq = ({
  questions = {},
  userResponses = {},
  maxHeight,
  onUserResponseChange,
  onBack,
  onCancel,
  onNext,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [containerRef, containerSize] = useElementSize();
  const [actionPanelRef, actionPanelSize] = useElementSize();

  const [errors, setErrors] = useState<any>({});

  const setMainReq = (response: string) => {
    onUserResponseChange('mainRequirements', [response]);
    if (response === 'iotTelemetry' || response === 'carbonMRV') {
      onUserResponseChange('documentProps', []);
      onUserResponseChange('sourceLanguages', '');
    }
  };

  const setDocumentProps = (response: string[]) => {
    onUserResponseChange('documentProps', response);
  };

  const setSourceLanguages = (response: string) => {
    onUserResponseChange('sourceLanguages', response);
  };

  const handleNext = () => {
    const errors: any = {};
    let hasError = false;
    if (!userResponses.mainRequirements) {
      errors.mainRequirements = t('common.errors.requiredField');
      hasError = true;
    }
    if ((userResponses.documentProps || []).includes('multiLanguage') && !userResponses.sourceLanguages) {
      errors.sourceLanguages = t('common.errors.requiredField');
      hasError = true;
    }
    setErrors(errors);
    if (!hasError) {
      onNext();
    }
  };

  const getQuestionDef = (qId: string) => {
    if (!questions) return null;
    if (Array.isArray(questions)) {
      for (const item of questions) {
        if (item.id === qId) return item;
        if (item.questions && Array.isArray(item.questions)) {
          const found = item.questions.find((q: any) => q.id === qId);
          if (found) return found;
        }
      }
    } else if (typeof questions === 'object') {
      return questions[qId] || null;
    }
    return null;
  };

  const mainReqDef = getQuestionDef('mainRequirements');
  const docPropsDef = getQuestionDef('documentProps');
  const sourceLangDef = getQuestionDef('sourceLanguages');

  return (
    <Box
      ref={containerRef}
      sx={{
        height: `${maxHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack alignItems={'flex-start'} spacing={4}>
          <RadioGroup
            label={mainReqDef?.questionText || mainReqDef?.question || 'What is your primary requirement?'}
            value={userResponses?.mainRequirements?.[0] || userResponses?.mainRequirements || ''}
            options={mainReqDef?.options || []}
            required
            error={errors.mainRequirements}
            fullWidth={false}
            onChange={setMainReq}
          />

          {(userResponses?.mainRequirements?.[0] === 'extractInfo' ||
            userResponses?.mainRequirements?.[0] === 'qna' ||
            userResponses?.mainRequirements?.[0] === 'extractSummary') && (
            <CheckboxGroup
              label={docPropsDef?.questionText || docPropsDef?.question || 'Document Properties'}
              values={userResponses?.documentProps || []}
              options={docPropsDef?.options || []}
              required
              error={errors.documentProps}
              fullWidth={false}
              onChange={setDocumentProps}
            />
          )}
          {(userResponses?.documentProps || []).includes('multiLanguage') && (
            <TextField
              label={sourceLangDef?.questionText || sourceLangDef?.question || 'Source Languages'}
              value={userResponses?.sourceLanguages || ''}
              required
              error={errors.sourceLanguages}
              fullWidth={false}
              onChange={setSourceLanguages}
            />
          )}
        </Stack>
      </Box>

      {/* Action bar */}
      <Box
        ref={actionPanelRef}
        sx={{
          px: 3,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <Button
            variant="text"
            onClick={onCancel}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            {t('common.buttons.cancel')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNext}
            disabled={!userResponses.mainRequirements}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 2.5,
              color: 'text.primary',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            {t('common.buttons.next')}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default StepMainReq;
